import React from 'react';
import { FreeVisual } from './FreeVisual';
import { ObjectVisualContent } from './ObjectVisualContent';
import { makeZoneDropHandlers } from './zoneDropHandlers';

/**
 * Renders the "Page Header (every page)" and "Page Footer (every page)" sky-colored drop zones.
 * Repeats on every page in the printed/exported report.
 *
 * `kind` is 'header' | 'footer' (matches the `_zone` value on visuals).
 * Header includes a parameter auto-display strip at the bottom.
 */
export function PageZoneArea({
  kind, reportHeader, reportFooter,
  visuals, visualResults,
  activeVisual, setActiveVisual,
  updateVisual, setSavedClean,
  handleVisualContextMenu,
  getTransformedData,
  getMaxZIndex, setVisuals,
  allFields, formatValue,
  parameters, paramValues,
}) {
  const isHeader = kind === 'header';
  const config = isHeader ? reportHeader : reportFooter;
  if (!config?.enabled) return null;

  const wrapperClass = isHeader ? 'mb-3' : 'mt-3';
  const label = isHeader ? 'Page Header (every page)' : 'Page Footer (every page)';
  const placeholder = isHeader
    ? 'Add visuals here — they repeat on every page'
    : 'Add visuals here — they repeat on every page (page numbers, disclaimers)';
  const variant = isHeader ? 'page-header' : 'page-footer';
  const testId = isHeader ? 'report-header-zone' : 'report-footer-zone';

  const drop = makeZoneDropHandlers({
    zone: kind,
    getMaxZIndex, setVisuals, setActiveVisual,
    reportHeader, reportFooter,
  });

  const zoneVisuals = visuals.filter(v => v._zone === kind);
  const showParams = isHeader && parameters?.length > 0 && zoneVisuals.length > 0;

  return (
    <div
      className={`${wrapperClass} border-2 border-dashed border-sky-400/30 rounded-xl relative bg-sky-500/5`}
      style={{ minHeight: config.height }}
      data-testid={testId}
      onDragOver={drop.onDragOver}
      onDragLeave={drop.onDragLeave}
      onDrop={drop.onDrop}
    >
      <span className="absolute top-1 left-2 text-[8px] text-sky-400/60 font-semibold uppercase">{label}</span>
      {zoneVisuals.length === 0 && (
        <div className="flex items-center justify-center h-full text-[10px] text-sky-400/40 py-4">{placeholder}</div>
      )}
      {zoneVisuals.map(v => {
        const vResult = (visualResults[v.id] || {}).result;
        const vData = getTransformedData(v.id) || vResult?.data || [];
        const fp = v.freePosition || { x: 10, y: isHeader ? 10 : 5, w: 200, h: Math.max(isHeader ? 24 : 20, (config.height || 40) - (isHeader ? 20 : 10)) };
        return (
          <FreeVisual
            key={v.id}
            position={fp}
            zIndex={v.zIndex || 10}
            isActive={activeVisual === v.id}
            onSelect={() => setActiveVisual(v.id)}
            onPositionChange={(pos) => { updateVisual(v.id, { freePosition: pos }); setSavedClean(false); }}
            onContextMenu={(e) => handleVisualContextMenu(e, v.id)}
          >
            <ObjectVisualContent
              v={v} onUpdate={(patch) => updateVisual(v.id, patch)} variant={variant}
              vData={vData} allFields={allFields} formatValue={formatValue}
            />
          </FreeVisual>
        );
      })}
      {showParams && (
        <div className="absolute bottom-1 left-2 right-2 flex flex-wrap gap-x-4 gap-y-0.5">
          {parameters.map(p => (
            <span key={p.name} className="text-[8px] text-muted-foreground/80">
              {p.label || p.name}: <span className="text-foreground/70">{paramValues[p.name] || p.defaultValue || 'All'}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
